import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";

import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import MenuIcon from "@mui/icons-material/Menu";

import AOI from "../Map/AOI";
import DataExplorerWrapper from "../DataExplorer/DataExplorerWrapper";
import LayerControl from "./LayerControl";
import LayerControlGroup from "./LayerControlGroup";
import OverlaysLegend from "./OverlaysLegend";
import PointLayerLegend from "./PointLayerLegend";

import { initialLayerState } from "../../config/config.layers";
import { controlMenuPosition } from "../../config/config.layout";
import { iconButtonStyle, styles } from "../../config/config.theme";

import classes from "./ControlMenu.module.css";

const baseRoute = process.env.REACT_APP_BASE_ROUTE
  ? process.env.REACT_APP_BASE_ROUTE
  : "/";

function ControlMenu() {
  const aoiState = useSelector((state) => state.aoi);
  const layerGroups = useSelector((state) => state.layerGroup);
  const layerState = useSelector((state) => state.layers);
  const [showMenu, setShowMenu] = useState(true);

  const [showChangelog, setShowChangelog] = useState(false);
  const [changelogContent, setChangelogContent] = useState("");
  const [width, setWidth] = useState("360px");

  const toggleMenuVisibility = () => {
    setShowMenu((prevState) => !prevState);
  };
  const handleTabClick = (tab) => {
    if (tab === "datasets") {
      if (window.innerWidth > 768) {
        setWidth("600px");
        return;
      }
    }
    setWidth("360px");
  };
  const handleToggleChangelog = () => {
    setShowChangelog((prevState) => !prevState);
  };

  useEffect(() => {
    fetch("../.." + baseRoute + "CHANGELOG.md")
      .then((res) => res.text())
      .then((text) => setChangelogContent(text));
  }, []);

  // layerControlGroup logic
  const activeLayerGroups = layerGroups?.allIds
    ?.filter((id) => {
      // true when the current layerGroup's associated AOIs are included in the current AOI selection
      const aoiSelectionActive =
        aoiState.selection.category &&
        aoiState.selection.selectedIds.length > 0;

      if (!aoiSelectionActive) {
        return true;
      } else if (layerGroups?.[id].aoiIds) {
        return layerGroups?.[id].aoiIds
          .map(
            (aoi) =>
              aoiState.selection.category === aoi.category &&
              aoiState.selection.selectedIds.includes(aoi.id)
          )
          .some((v) => v);
      } else {
        return false;
      }
    })
    // NGWOS National Projects at the top, sort the rest alphabetically
    .sort((a, b) =>
      a === "NGWOS National Projects"
        ? -1
        : b === "NGWOS National Projects"
        ? 1
        : a.localeCompare(b)
    );

  return (
    <>
      <IconButton
        aria-label="open control menu"
        variant="outlined"
        onClick={toggleMenuVisibility}
        sx={{
          ...iconButtonStyle,
          top: "10px",
          right: controlMenuPosition.buttonRight
            ? controlMenuPosition.buttonRight
            : "",
          left: controlMenuPosition.buttonLeft
            ? controlMenuPosition.buttonLeft
            : "",
          position: "absolute",
          m: 0,
          zIndex: "101",
          "&:hover": { backgroundColor: styles["background-primary"] },
          "&:active": { backgroundColor: styles["background-primary"] },
          "&:focus": { backgroundColor: styles["background-primary"] },
        }}
      >
        {!showMenu && <MenuIcon fontSize="1.5rem" color="primary" />}
        {showMenu && (
          <KeyboardArrowLeftIcon fontSize="1.5rem" color="primary" />
        )}
      </IconButton>

      {showMenu ? (
        <Box
          id="control-menu"
          sx={{
            right: controlMenuPosition.right ? controlMenuPosition.right : "",
            left: controlMenuPosition.left ? controlMenuPosition.left : "",
            width,
            boxSizing: "border-box",
            position: "absolute",
            zIndex: 100,
            top: "10px",
            height: "auto",
            backgroundColor: "background.main",
            px: 0,
            pt: 5,
            overflow: "hidden",
          }}
        >
          {/* tabs start here  */}
          <Tabs>
            <TabList>
              <Tab onClick={() => handleTabClick("projects")}>Projects</Tab>
              <Tab onClick={() => handleTabClick("datasets")}>Datasets</Tab>
              <Tab onClick={() => handleTabClick("sitesAndFilters")}>
                Filters
              </Tab>
              <Tab onClick={() => handleTabClick("legend")}>Legend</Tab>
            </TabList>
            <TabPanel>
              {/* theme/layer selection tab content  */}
              <div className={classes["menu-content"]}>
                {activeLayerGroups
                  ? activeLayerGroups.map((id) => {
                      const group = layerGroups[id];
                      return (
                        <React.Fragment key={id}>
                          <LayerControlGroup
                            name={id}
                            icon={group.icon}
                            activeLayerGroups={activeLayerGroups}
                          />
                          {group.divider && <Divider />}
                        </React.Fragment>
                      );
                    })
                  : null}
              </div>
            </TabPanel>
            <TabPanel>
              <div
                className={classes["menu-content"]}
                style={{
                  height: "calc(100vh - 245px)",
                  flex: "1 1 auto",
                  display: "flex",
                }}
              >
                <DataExplorerWrapper
                  showFilters={false}
                  useMobileLayout
                  tabsList={["datasets"]}
                />
              </div>
            </TabPanel>
            <TabPanel>
              <div className={classes["menu-content"]}>
                {Object.keys(initialLayerState).map((layerId) => (
                  <LayerControl key={layerId} id={layerId} />
                ))}
                <AOI />
              </div>
            </TabPanel>
            <TabPanel>
              <div className={classes["menu-content"]}>
                <div className={classes["legend-section"]}>
                  {Object.keys(layerState).map((id) => {
                    const layer = layerState[id];
                    return (
                      layer.showLayer && (
                        <React.Fragment key={id}>
                          <PointLayerLegend id={id} layerName={layer.name} />
                        </React.Fragment>
                      )
                    );
                  })}
                  <Divider />
                  <OverlaysLegend />
                </div>
              </div>
            </TabPanel>
          </Tabs>
          <div className={classes["version-container"]}>
            <p
              onClick={handleToggleChangelog}
            >{`v${process.env.REACT_APP_VERSION} See what's new`}</p>
          </div>
          <Dialog
            open={showChangelog}
            onClose={handleToggleChangelog}
            maxWidth="md"
            sx={{ zIndex: 9999 }}
          >
            <DialogTitle sx={{ textAlign: "center" }}>Changelog</DialogTitle>
            <DialogContent>
              <ReactMarkdown children={changelogContent} />
            </DialogContent>
            <DialogActions>
              <Button
                aria-label="close overlay selection"
                onClick={() => setShowChangelog(false)}
                color="secondary"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      ) : null}
    </>
  );
}

export default ControlMenu;
